import {FC, useState, useCallback, useMemo} from 'react';

import {isEmpty} from 'lodash';
import {isBlankOrEmpty} from '@/engine/utils/string';
import {useUpdateUserProfile} from '@/hooks/queries/users';
import {UserProfileCompletionStatus, UserProfileInput, AdvisorRole} from '@/repositories/beneva-api/endpoints/users';
import {Props, UserProfileContext} from './user-profile-context';

const IGNORED_FIELDS = ['cons26_advisorType', 'cons27_advisorRole'];

export const UserProfileContextProvider: FC<Props> = ({children, profile: profileProp}) => {
  const {mutateAsync: updateUserProfile} = useUpdateUserProfile();
  const [profile, setProfile] = useState(profileProp);

  const isEmployee = useMemo(() => profile.advisorRole === AdvisorRole.Employee, [profile]);
  const isAssistant = useMemo(() => profile.advisorRole === AdvisorRole.Assistant, [profile]);
  const isAdvisor = useMemo(() => profile.advisorRole === AdvisorRole.Advisor, [profile]);

  const differentFields = useMemo(() => {
    const fields = profile.completionMetadata.differentFields;
    return fields.filter(fieldId => !IGNORED_FIELDS.includes(fieldId));
  }, [profile]);

  const isVerified = useMemo(
    () => profile.completionMetadata.status === UserProfileCompletionStatus.Complete && isEmpty(differentFields),
    [profile, differentFields],
  );

  const isAdvisorWithUnconfirmedStatus = useMemo(() => {
    return (
      isAdvisor &&
      [
        profile.cons25_advisorCode,
        profile.cons12_altAdvisorCode1,
        profile.cons13_altAdvisorCode2,
        profile.cons14_altAdvisorCode3,
        profile.cons15_altAdvisorCode4,
      ].every(isBlankOrEmpty)
    );
  }, [isAdvisor, profile]);

  const isAssistantWithoutAdvisor = useMemo(
    () => !!isAssistant && isEmpty(profile.delegatedAdvisors),
    [isAssistant, profile.delegatedAdvisors],
  );

  const saveProfile = useCallback(
    async (newProfile: UserProfileInput) => {
      const response = await updateUserProfile(newProfile);
      setProfile(response.data);
      return response.data;
    },
    [updateUserProfile],
  );

  return (
    <UserProfileContext.Provider
      value={{
        profile,
        differentFields,
        isVerified,
        saveProfile,
        isEmployee,
        isAssistant,
        isAdvisor,
        isAdvisorWithUnconfirmedStatus,
        isAssistantWithoutAdvisor,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};
